<template>
  <div class="bg-white rounded-lg shadow-md p-6 h-full">
    <h3 class="text-lg font-semibold text-gray-700 mb-6">Estadísticas del Período</h3>
    
    <div v-if="estadisticas" class="grid grid-cols-2 gap-4">
      <!-- Total de Pedidos -->
      <div class="bg-blue-50 rounded-lg p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 mb-1">Total de Pedidos</p>
            <p class="text-3xl font-bold text-blue-600">{{ estadisticas.total_cotizaciones }}</p>
          </div>
          <div class="bg-blue-100 rounded-full p-3">
            <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
          </div>
        </div>
      </div>

      <!-- Pedidos a Tiempo -->
      <div class="bg-green-50 rounded-lg p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 mb-1">A Tiempo</p>
            <p class="text-3xl font-bold text-green-600">{{ estadisticas.cotizaciones_a_tiempo }}</p>
          </div>
          <div class="bg-green-100 rounded-full p-3">
            <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
        </div>
      </div>

      <!-- Pedidos Retrasados -->
      <div class="bg-red-50 rounded-lg p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 mb-1">Retrasados</p>
            <p class="text-3xl font-bold text-red-600">{{ estadisticas.cotizaciones_retrasadas }}</p>
          </div>
          <div class="bg-red-100 rounded-full p-3">
            <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
        </div>
      </div>

      <!-- Porcentaje de Cumplimiento -->
      <div :class="cumplimientoClases" class="rounded-lg p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 mb-1">% Cumplimiento</p>
            <p :class="porcentajeClases" class="text-3xl font-bold">
              {{ estadisticas.porcentaje_cumplimiento.toFixed(2) }}%
            </p>
          </div>
          <div :class="iconoCumplimientoClases" class="rounded-full p-3">
            <svg class="w-8 h-8" :class="porcentajeClases" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
            </svg>
          </div>
        </div>
        <div class="mt-3 flex items-center text-xs">
          <span class="text-gray-600">Meta requerida: </span>
          <span class="ml-1 font-semibold text-gray-800">{{ estadisticas.meta_requerida }}%</span>
        </div>
      </div>
    </div>

    <!-- Indicador Visual de Meta -->
    <div v-if="estadisticas" class="mt-6 bg-gray-50 rounded-lg p-4">
      <div class="flex items-center justify-between mb-2">
        <span class="text-sm font-medium text-gray-700">Progreso hacia la meta</span>
        <span class="text-sm font-semibold" :class="porcentajeClases">
          {{ estadisticas.porcentaje_cumplimiento.toFixed(2) }}% / {{ estadisticas.meta_requerida }}%
        </span>
      </div>
      <div class="w-full bg-gray-200 rounded-full h-3 relative overflow-hidden">
        <!-- Barra de progreso -->
        <div 
          :class="barraProgresoClases"
          class="h-3 rounded-full transition-all duration-500 ease-out"
          :style="{ width: Math.min(estadisticas.porcentaje_cumplimiento, 100) + '%' }"
        ></div>
        <!-- Línea de meta -->
        <div 
          class="absolute top-0 h-3 w-1 bg-gray-800 opacity-50"
          :style="{ left: estadisticas.meta_requerida + '%' }"
          title="Meta 95%"
        ></div>
      </div>
      <div class="flex justify-between mt-1">
        <span class="text-xs text-gray-500">0%</span>
        <span class="text-xs text-gray-500">100%</span>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'EstadisticasBono',
  props: {
    estadisticas: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const cumplimientoClases = computed(() => {
      if (!props.estadisticas) return 'bg-gray-50';
      
      const cumplimiento = props.estadisticas.porcentaje_cumplimiento;
      const meta = props.estadisticas.meta_requerida;
      
      if (cumplimiento >= meta) return 'bg-green-50';
      if (cumplimiento >= meta - 5) return 'bg-yellow-50';
      return 'bg-red-50';
    });

    const iconoCumplimientoClases = computed(() => {
      if (!props.estadisticas) return 'bg-gray-100';
      
      const cumplimiento = props.estadisticas.porcentaje_cumplimiento;
      const meta = props.estadisticas.meta_requerida;
      
      if (cumplimiento >= meta) return 'bg-green-100';
      if (cumplimiento >= meta - 5) return 'bg-yellow-100';
      return 'bg-red-100';
    });

    const porcentajeClases = computed(() => {
      if (!props.estadisticas) return 'text-gray-600';
      
      const cumplimiento = props.estadisticas.porcentaje_cumplimiento;
      const meta = props.estadisticas.meta_requerida;
      
      if (cumplimiento >= meta) return 'text-green-600';
      if (cumplimiento >= meta - 5) return 'text-yellow-600';
      return 'text-red-600';
    });

    const barraProgresoClases = computed(() => {
      if (!props.estadisticas) return 'bg-gray-400';
      
      const cumplimiento = props.estadisticas.porcentaje_cumplimiento;
      const meta = props.estadisticas.meta_requerida;
      
      if (cumplimiento >= meta) return 'bg-green-500';
      if (cumplimiento >= meta - 5) return 'bg-yellow-500';
      return 'bg-red-500';
    });

    return {
      cumplimientoClases,
      iconoCumplimientoClases,
      porcentajeClases,
      barraProgresoClases,
    };
  },
};
</script>
